import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FormattedMessage, useIntl } from "react-intl";
import { authApi } from "@/lib/api/auth";
import loginImage from "@/assets/exercise-background-photos/login-page-2.png";
import { AuthFormCard } from "@/components/AuthFormCard";
import { AuthHeader } from "@/components/AuthHeader";
import { AuthBackButton } from "@/components/AuthBackButton";
import { AuthErrorAlert } from "@/components/AuthErrorAlert";
import { AuthTextInput } from "@/components/AuthTextInput";
import { AuthFooterLink } from "@/components/AuthFooterLink";

export const Route = createFileRoute("/auth/forgot-password")({
  component: ForgotPassword,
});

function ForgotPassword() {
  const navigate = useNavigate();
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const forgotSchema = z.object({
    nickname: z
      .string()
      .min(1, intl.formatMessage({ id: "validation.nickname.required" })),
  });

  type ForgotFormData = z.infer<typeof forgotSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotFormData>({ resolver: zodResolver(forgotSchema) });

  const onSubmit = async (data: ForgotFormData) => {
    setError(null);
    setIsLoading(true);
    try {
      await authApi.forgotPassword(data.nickname);
      setSent(true);
    } catch {
      setError(intl.formatMessage({ id: "forgotPassword.error" }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col relative">
      <AuthHeader
        image={loginImage}
        imageAlt="Forgot Password"
        title={<FormattedMessage id="forgotPassword.title" />}
        subtitle={<FormattedMessage id="forgotPassword.subtitle" />}
      />

      <AuthFormCard>
        <div className="flex flex-col">
          <AuthBackButton onClick={() => navigate({ to: "/trainer/auth/login" })} />

          {sent ? (
            <div className="space-y-4">
              <p className="text-center text-small text-dark">
                <FormattedMessage id="forgotPassword.sent" />
              </p>
              <p className="text-center text-xs text-muted-foreground">
                <FormattedMessage
                  id="forgotPassword.hint"
                  values={{
                    link: (chunks) => (
                      <a
                        href="mailto:contact@hamd.life"
                        className="underline"
                      >
                        {chunks}
                      </a>
                    ),
                  }}
                />
              </p>
              <Button
                type="button"
                className="w-full"
                onClick={() => navigate({ to: "/trainer/auth/login" })}
              >
                <FormattedMessage id="forgotPassword.backToLogin" />
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {error && <AuthErrorAlert>{error}</AuthErrorAlert>}

              <AuthTextInput
                {...register("nickname")}
                type="text"
                placeholder={intl.formatMessage({
                  id: "login.nickname.placeholder",
                })}
                disabled={isLoading}
                onFocus={() => setError(null)}
                error={errors.nickname?.message}
              />

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <FormattedMessage id="forgotPassword.sending" />
                ) : (
                  <FormattedMessage id="forgotPassword.submit" />
                )}
              </Button>

              <AuthFooterLink
                promptId="forgotPassword.rememberPrompt"
                linkId="forgotPassword.backToLogin"
                to="/trainer/auth/login"
              />
            </form>
          )}
        </div>
      </AuthFormCard>
    </div>
  );
}
